import { useMemo } from "react";
import { differenceInCalendarDays, format, max, min } from "date-fns";
import { useCases } from "@/domains/cases";
import { useAssets, useReservations } from "@/domains/assets";
import { assetTypeLabel } from "@/domains/assets";
import type { AssetType } from "@/domains/assets";
import type {
  AssetUtilizationRow,
  DateRange,
  DeploymentByDistrict,
  DeploymentByType,
  KpiSummary,
  ProcessingTimeBucket,
  RentedAssetRow,
  ReportRow,
  TaskTimingPoint,
} from "@/domains/reporting/api/types";

type KpiData = {
  isPending: boolean;
  summary: KpiSummary;
  processingByMonth: ReadonlyArray<ProcessingTimeBucket>;
  byType: ReadonlyArray<DeploymentByType>;
  byDistrict: ReadonlyArray<DeploymentByDistrict>;
  taskTiming: ReadonlyArray<TaskTimingPoint>;
  reportRows: ReadonlyArray<ReportRow>;
  utilization: ReadonlyArray<AssetUtilizationRow>;
  currentlyRented: ReadonlyArray<RentedAssetRow>;
};

const ASSET_TYPES: ReadonlyArray<AssetType> = [
  "camera",
  "mobileTree",
  "camcar",
  "batteryPack",
  "trailer",
];

function isAssetType(value: string): value is AssetType {
  return (ASSET_TYPES as ReadonlyArray<string>).includes(value);
}

function overlapDays(
  rangeFrom: Date,
  rangeTo: Date,
  start: Date,
  end: Date,
): number {
  if (end < rangeFrom || start > rangeTo) return 0;
  const clippedStart = max([start, rangeFrom]);
  const clippedEnd = min([end, rangeTo]);
  return Math.max(0, differenceInCalendarDays(clippedEnd, clippedStart) + 1);
}

const TODAY = "2026-05-11";

export function useKpiData(range: DateRange): KpiData {
  const cases = useCases();
  const assets = useAssets();
  const reservations = useReservations();

  return useMemo<KpiData>(() => {
    const list = cases.data ?? [];
    const assetList = assets.data ?? [];
    const reservationList = reservations.data ?? [];

    const rangeFrom = new Date(range.from);
    const rangeTo = new Date(range.to);
    const daysInRange = Math.max(
      1,
      differenceInCalendarDays(rangeTo, rangeFrom) + 1,
    );

    const inRangeList = list.filter((c) => {
      const cStart = new Date(c.startDate);
      const cEnd = new Date(c.endDate);
      return cEnd >= rangeFrom && cStart <= rangeTo;
    });

    const totalDeployments = inRangeList.length;
    const activeDeployments = inRangeList.filter((c) => c.status === "active").length;

    const durations = inRangeList.map((c) =>
      Math.max(1, differenceInCalendarDays(new Date(c.endDate), new Date(c.startDate))),
    );
    const averageProcessingDays = durations.length
      ? Math.round((durations.reduce((s, n) => s + n, 0) / durations.length) * 10) / 10
      : 0;

    const monthMap = new Map<string, { sum: number; count: number }>();
    for (const c of inRangeList) {
      const month = format(new Date(c.startDate), "yyyy-MM");
      const days = Math.max(
        1,
        differenceInCalendarDays(new Date(c.endDate), new Date(c.startDate)),
      );
      const cur = monthMap.get(month) ?? { sum: 0, count: 0 };
      monthMap.set(month, { sum: cur.sum + days, count: cur.count + 1 });
    }
    const processingByMonth: ReadonlyArray<ProcessingTimeBucket> = Array.from(monthMap.entries())
      .map(([month, v]) => ({
        month,
        averageDays: Math.round((v.sum / v.count) * 10) / 10,
        count: v.count,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));

    const typeMap = new Map<string, number>();
    const districtMap = new Map<string, number>();
    for (const c of inRangeList) {
      const cnt = districtMap.get(c.district) ?? 0;
      districtMap.set(c.district, cnt + 1);
      for (const aid of c.assetIds) {
        const asset = assetList.find((a) => a.id === aid);
        if (!asset) continue;
        const k = asset.type;
        typeMap.set(k, (typeMap.get(k) ?? 0) + 1);
      }
    }
    const byType: ReadonlyArray<DeploymentByType> = Array.from(typeMap.entries())
      .map(([type, count]) => ({
        type,
        label: assetTypeLabel(isAssetType(type) ? type : "camera"),
        count,
      }))
      .sort((a, b) => b.count - a.count);

    const byDistrict: ReadonlyArray<DeploymentByDistrict> = Array.from(districtMap.entries())
      .map(([district, count]) => ({ district, count }))
      .sort((a, b) => b.count - a.count);

    const taskTiming: ReadonlyArray<TaskTimingPoint> = [
      { category: "Plaatsing", averageHours: 4.2 },
      { category: "Administratie", averageHours: 1.8 },
      { category: "Operaties", averageHours: 2.6 },
      { category: "Externe partner", averageHours: 18.4 },
      { category: "Ophaling", averageHours: 3.1 },
    ];

    const averageResponseHours = 12.5;

    const reportRows: ReadonlyArray<ReportRow> = inRangeList.map((c) => {
      const linkedAssets = assetList.filter((a) => c.assetIds.includes(a.id));
      const types = Array.from(new Set(linkedAssets.map((a) => assetTypeLabel(a.type)))).join(", ");
      return {
        caseNumber: c.number,
        title: c.title,
        type: types || "—",
        address: c.address,
        district: c.district,
        startDate: c.startDate,
        endDate: c.endDate,
        durationDays: Math.max(
          1,
          differenceInCalendarDays(new Date(c.endDate), new Date(c.startDate)),
        ),
        assetCount: c.assetIds.length,
        reason: c.reason,
      };
    });

    const utilization: ReadonlyArray<AssetUtilizationRow> = assetList
      .map((asset) => {
        const assetReservations = reservationList.filter((r) => r.assetId === asset.id);
        let daysUsed = 0;
        let deploymentCount = 0;
        for (const r of assetReservations) {
          const rStart = new Date(r.startDate);
          const rEnd = new Date(r.endDate);
          const overlap = overlapDays(rangeFrom, rangeTo, rStart, rEnd);
          if (overlap > 0) {
            daysUsed += overlap;
            deploymentCount += 1;
          }
        }
        return {
          assetId: asset.id,
          number: asset.number,
          name: asset.name,
          type: asset.type,
          typeLabel: assetTypeLabel(asset.type),
          deploymentCount,
          daysUsed,
          daysInRange,
          utilizationPct: Math.round((daysUsed / daysInRange) * 100),
          averageDaysPerDeployment:
            deploymentCount === 0
              ? 0
              : Math.round((daysUsed / deploymentCount) * 10) / 10,
        };
      })
      .sort((a, b) => b.utilizationPct - a.utilizationPct);

    const today = new Date(TODAY);
    const currentlyRented: ReadonlyArray<RentedAssetRow> = reservationList
      .filter((r) => {
        const rStart = new Date(r.startDate);
        const rEnd = new Date(r.endDate);
        return rStart <= today && rEnd >= today;
      })
      .map((r) => {
        const asset = assetList.find((a) => a.id === r.assetId);
        const caseFile = list.find((c) => c.id === r.caseId);
        const rEnd = new Date(r.endDate);
        return {
          assetId: r.assetId,
          number: asset?.number ?? r.assetId,
          name: asset?.name ?? "",
          typeLabel: asset ? assetTypeLabel(asset.type) : "—",
          caseNumber: caseFile?.number ?? r.caseId,
          caseTitle: caseFile?.title ?? "",
          address: caseFile?.address ?? "",
          district: caseFile?.district ?? "",
          startDate: r.startDate,
          endDate: r.endDate,
          daysRemaining: Math.max(0, differenceInCalendarDays(rEnd, today)),
        };
      })
      .sort((a, b) => a.endDate.localeCompare(b.endDate));

    return {
      isPending: cases.isPending || assets.isPending || reservations.isPending,
      summary: {
        averageProcessingDays,
        averageResponseHours,
        totalDeployments,
        activeDeployments,
      },
      processingByMonth,
      byType,
      byDistrict,
      taskTiming,
      reportRows,
      utilization,
      currentlyRented,
    };
  }, [
    cases.data,
    cases.isPending,
    assets.data,
    assets.isPending,
    reservations.data,
    reservations.isPending,
    range.from,
    range.to,
  ]);
}
